import { api, isApiError } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  oauthConsentRequestDtoSchema,
  oauthDecisionResultDtoSchema,
  oauthGrantListDtoSchema,
  type OauthConsentRequestDto,
  type OauthDecisionResultDto,
  type OauthGrantListDto,
} from '@lib/dto/oauth-consent.dto'
import type { OauthConsentDecisionInput } from '@lib/input/oauth-consent.input'
import type { IOauthService } from '@/types/OauthService.types'

/**
 * Session routes for connecting an assistant and for disconnecting one later.
 * Responses are validated against the shared `@lib` schemas at the boundary.
 */
class OauthService implements IOauthService {
  async getRequest(id: string): Promise<OauthConsentRequestDto> {
    try {
      return await api.get(
        `/oauth/requests/${encodeURIComponent(id)}`,
        oauthConsentRequestDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load connection request', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async decide(
    id: string,
    input: OauthConsentDecisionInput,
  ): Promise<OauthDecisionResultDto> {
    try {
      return await api.post(
        `/oauth/requests/${encodeURIComponent(id)}/decision`,
        input,
        oauthDecisionResultDtoSchema,
      )
    } catch (error) {
      // The success body is the return address, and it carries the code.
      // A parse failure quotes that body in its message, so log the status
      // only — never the message.
      logger.error('Failed to answer a connection request', {
        status: isApiError(error) ? error.status : null,
      })
      throw error
    }
  }

  async listGrants(): Promise<OauthGrantListDto> {
    try {
      return await api.get('/oauth/grants', oauthGrantListDtoSchema)
    } catch (error) {
      logger.error('Failed to list connected apps', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async revokeGrant(id: string): Promise<void> {
    try {
      await api.postNoContent(
        `/oauth/grants/${encodeURIComponent(id)}/revoke`,
        {},
      )
    } catch (error) {
      logger.error('Failed to disconnect an app', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const oauthService = new OauthService()
